/* eslint-disable no-console */
/* eslint-disable import/prefer-default-export */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable import/extensions */
import {
  setupEventContextTrace,
  wrapContextManagerClass,
  setOpenTelemetryContextAsyncContextStrategy,
} from "@sentry/opentelemetry";
import { SpanProcessor } from "@opentelemetry/sdk-trace-web";
import { ContextManager, SpanStatusCode } from "@opentelemetry/api";
import { ATTR_OTEL_STATUS_CODE } from "@opentelemetry/semantic-conventions";
import { OtelTracerProvider } from "../core";
import { SentryOtelTracerProviderConfig } from "./types";
import { getSentryClient } from "./sentry-utils";
import { FilterableSentrySpanProcessor } from "./filterable-sentry-span-processor";
import { OTelTracerProviderEnrichContextManager } from "../core/types";
import { OTEL_AUTO_INSTRUMENTS_SPANS } from "./constants";
import { getEnvironment } from "../../utils/environment";
import getFormattedEnvironment from "../../utils/helpers";

/**
 * Sentry OpenTelemetry tracer provider for Sentry backend.
 * Since Sentry does not support OpenTelemetry natively, this provider is used for Sentry to bridge the gap between OpenTelemetry and Sentry's tracing system.
 *
 * For OpenTelemetry-supported backends, use the `CommonOtelTracerProvider` instead.
 */
export class SentryOtelTracerProvider implements OtelTracerProvider {
  private readonly config: SentryOtelTracerProviderConfig;

  constructor(config: SentryOtelTracerProviderConfig) {
    this.config = config;
  }

  setup() {
    const env = getEnvironment() === "local" ? "Development" : getEnvironment();

    const client = getSentryClient(
      this.config.dsn,
      getFormattedEnvironment(env),
    );

    if (!client) {
      console.warn("Sentry client initialization failed");
      return;
    }

    // behind the scenes to make OTel telemetry compatible with Sentry
    setupEventContextTrace(client);

    const spanProcessors: SpanProcessor[] = [
      new FilterableSentrySpanProcessor((span) => {
        if (
          span.attributes[ATTR_OTEL_STATUS_CODE] === "ERROR" ||
          span.status.code === SpanStatusCode.ERROR ||
          span.attributes[OTEL_AUTO_INSTRUMENTS_SPANS] === false
        ) {
          return true; // Only send spans with errors
        }
        return false; // Skip other spans
      }),
    ];

    // wrap an OpenTelemetry ContextManager in a way that ensures the context is kept in sync with the Sentry Scope.
    const enrichContextManager: OTelTracerProviderEnrichContextManager = ({
      contextManagerClass,
    }): ContextManager => {
      const SentryContextManager = wrapContextManagerClass(contextManagerClass);
      return new SentryContextManager();
    };

    setOpenTelemetryContextAsyncContextStrategy();

    // eslint-disable-next-line consistent-return
    return {
      spanProcessors,
      enrichContextManager,
    };
  }
}
